import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  filter,
  flatMap,
  flatten,
  includes,
  isEmpty,
  join,
  map,
  orderBy,
  size,
  some,
  sortBy,
  uniq,
} from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import CopyableText from 'components/atoms/CopyableText';
import { AnnotationAssignment } from 'interfaces/annotation';
import { AnnotationStatsTable } from './AnnotationStatsTable';
import { useFilterAndFlattenAnnotationAssignments } from './helpers';
import { SelectSlidesByWorkingState } from './SelectSlidesByWorkingState';
import { SelectSlidesFromList } from './SelectSlidesFromList';
import { IFormValues } from './types';

interface Props {
  formMethods: UseFormReturn<Partial<IFormValues>, any>;
  annotationAssignments: AnnotationAssignment[];
}

export const SelectSlidesStep: React.FC<Props> = ({ formMethods, annotationAssignments }) => {
  const { watch } = formMethods;

  const selectedAssignmentIds = watch('annotationAssignmentIds');
  const slideIds = watch('slideIds') || [];
  const workingStatesToUse = watch('workingStatesToUse') as string[] | undefined;

  const filteredAnnotationAssignments = useFilterAndFlattenAnnotationAssignments({
    annotationAssignments,
    selectedAssignmentIds,
  });

  const slidesInAnnotationAssignments = useMemo(
    () =>
      orderBy(
        uniq(flatMap(filteredAnnotationAssignments, ({ annotations }) => map(annotations, 'slideId'))),
        [
          (slideId) =>
            join(
              sortBy(
                flatten(
                  map(
                    filter(filteredAnnotationAssignments, (assignment) => some(assignment.annotations, { slideId })),
                    'annotationAssignmentId'
                  )
                )
              ),
              ','
            ),
        ],
        ['asc']
      ),
    [filteredAnnotationAssignments]
  );
  const slideIdsNotInAssignments = filter(slideIds, (id) => !includes(slidesInAnnotationAssignments, id));

  const slideSelectionMode = workingStatesToUse ? 'byWorkingState' : 'bySlideId';
  const setSlideSelectionMode = (mode: 'byWorkingState' | 'bySlideId') => {
    formMethods.setValue('workingStatesToUse', mode === 'byWorkingState' ? [] : undefined);
    formMethods.setValue('slideIds', []);
  };

  return (
    <>
      <Grid item container direction="column" spacing={2} xs={6}>
        <Grid item>
          <RadioGroup
            value={slideSelectionMode}
            onChange={(event) => {
              setSlideSelectionMode(event.target.value as 'byWorkingState' | 'bySlideId');
            }}
          >
            <FormControlLabel value="bySlideId" control={<Radio />} label="Add slides by slide ID" />
            {slideSelectionMode === 'bySlideId' && (
              <SelectSlidesFromList
                formMethods={formMethods}
                annotationAssignments={annotationAssignments}
                slidesInAnnotationAssignments={slidesInAnnotationAssignments}
              />
            )}
            <FormControlLabel value="byWorkingState" control={<Radio />} label="Add slides by working state" />
            {slideSelectionMode === 'byWorkingState' && (
              <SelectSlidesByWorkingState formMethods={formMethods} annotationAssignments={annotationAssignments} />
            )}
          </RadioGroup>
        </Grid>
      </Grid>

      {!isEmpty(selectedAssignmentIds) && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CopyableText
              variant="caption"
              textForCopy={join(slidesInAnnotationAssignments, '\n')}
              tooltipText="Copy IDs of slides in selected assignment IDs"
            >
              Slides in selected annotation assignments: {size(slidesInAnnotationAssignments)}
            </CopyableText>
          </Grid>
          {/* Number of selected slides */}
          <Grid item xs={12}>
            <CopyableText variant="caption" textForCopy={join(slideIds, '\n')} tooltipText="Copy selected slide IDs">
              Selected slides: {size(slideIds)}
            </CopyableText>
          </Grid>
          {/* Number of slides not in selected annotation assignments */}
          <Grid item xs={12}>
            <CopyableText
              variant="caption"
              textForCopy={join(slideIdsNotInAssignments, '\n')}
              tooltipText="Copy slides not in selected annotation assignments"
            >
              Slides not in selected annotation assignments: {size(slideIdsNotInAssignments)}
            </CopyableText>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Annotation Assignments Summary</AccordionSummary>
          <AccordionDetails>
            <AnnotationStatsTable
              selectedSlideIds={slideIds}
              annotationAssignments={annotationAssignments}
              selectedAssignmentIds={selectedAssignmentIds}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
    </>
  );
};
